// Instanced rendering for the HIGH-COUNT model buildings (conveyor / drill / dual-drill / wall / ramp / splitter /
// cryo). A dense factory used to be ~520 individual meshes — one Group of 3–9 child meshes per building — which the
// renderer walked several times a frame (main + bloom occluder + shadow). Here every building TYPE keeps ONE
// InstancedMesh per model PART, so all 40 conveyors share ~3 draw calls, all 41 drills share ~5, etc. Placement writes
// an instance matrix; bulldoze swap-removes it. Animated parts (the cryo's two spinning wheels) get their matrix
// rewritten each frame; the cryo's blue coil also carries a PER-INSTANCE emissive so each cooler still pulses at its
// own brightness while sharing one material. That emissive is a VEC3, not a scalar, so one pool can also show N
// different neon COLORS — the magnetron's disk is unlit until a cryo caps it and turns it ice-blue.
// See [[longfight-basebuilding]] [[longfight-perf-probes]].

import * as THREE from "three";
import type { FactoryModel } from "./models";

/** How a pool paints one NEON part.
 *  A plain NUMBER is the classic fixed-hue neon: base color == emissive color == that hue, and the per-instance
 *  attribute only scales its BRIGHTNESS (all instances share one hue).
 *  The object form is for a part whose emissive HUE differs per building — the magnetron's disk inner is unlit
 *  brown normally and ice-blue under a cryo. There the surface keeps its authored `color`, the material bakes a
 *  WHITE `emissive`, and the per-instance vec3 carries hue×brightness outright. */
export interface NeonPart {
  color: number;           // base (lit) surface color
  emissive?: number;       // baked emissive color, multiplied per instance; defaults to `color`
  /** Take the bloom contribution from a separate BLOOM_LAYER-ONLY twin mesh whose output is zeroed wherever the
   *  instance emissive is 0. The Group path toggled `layers.enable/disable(BLOOM_LAYER)` per mesh, which an
   *  InstancedMesh cannot do per instance; without this an unlit instance would still bleed its LIT diffuse into
   *  the bloom pass (which renders BLOOM_LAYER objects with their real, lit material). */
  maskedBloom?: boolean;
}
export type NeonSpec = number | NeonPart;
const neonColor = (s: NeonSpec): number => (typeof s === "number" ? s : s.color);
const neonEmissive = (s: NeonSpec): number => (typeof s === "number" ? s : s.emissive ?? s.color);
const neonMasked = (s: NeonSpec): boolean => typeof s !== "number" && !!s.maskedBloom;

/** A material whose emissive is multiplied by a per-instance `instEmissive` VEC3 (injected via onBeforeCompile) —
 *  lets one InstancedMesh show N different neon brightnesses AND N different neon colors. A (v,v,v) attribute is
 *  exactly the old scalar behavior.
 *  `mask` builds the bloom-pass twin: identical shading, multiplied out to black on any instance whose emissive is
 *  zero, so an unlit instance adds nothing to the additive bloom composite. */
function neonMaterial(spec: NeonSpec, mask = false): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: neonColor(spec), emissive: neonEmissive(spec), emissiveIntensity: 1, roughness: 0.7 });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = "attribute vec3 instEmissive;\nvarying vec3 vInstEmissive;\n" +
      sh.vertexShader.replace("void main() {", "void main() {\n\tvInstEmissive = instEmissive;");
    sh.fragmentShader = "varying vec3 vInstEmissive;\n" +
      sh.fragmentShader.replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\n\ttotalEmissiveRadiance *= vInstEmissive;");
    if (mask) sh.fragmentShader = sh.fragmentShader.replace("#include <opaque_fragment>",
      "#include <opaque_fragment>\n\tgl_FragColor.rgb *= step(1e-5, dot(vInstEmissive, vec3(1.0)));");
  };
  m.customProgramCacheKey = () => (mask ? "factoryNeonMask" : "factoryNeon");
  return m;
}

interface PartIM {
  name: string;
  im: THREE.InstancedMesh;
  emit: THREE.InstancedBufferAttribute | null; // per-instance emissive color, rgb (neon parts only)
  /** BLOOM_LAYER-only twin of `im` for a `maskedBloom` part. Shares `im`'s instanceMatrix + `emit` attribute
   *  objects (same GPU buffers, one upload), so every write below reaches it for free — only `count` is mirrored. */
  twin: THREE.InstancedMesh | null;
}

/** Extra procedural part baked into a pool (e.g. the conveyor's direction arrow, which isn't in the OBJ). */
export interface ExtraPart { name: string; geometry: THREE.BufferGeometry; material: THREE.Material; }

/** One pool = all instances of ONE model. `owners[slot]` is the Placed that holds that slot, so a swap-remove can fix
 *  up the moved building's stored slot index. */
class Pool {
  parts: PartIM[] = [];
  owners: { inst?: { slot: number } | null }[] = [];
  count = 0;
  private _m = new THREE.Matrix4();

  constructor(root: THREE.Group, model: FactoryModel, private cap: number, opts: {
    neon?: Record<string, NeonSpec>; // partName -> neon spec (adds the per-instance emissive attribute)
    extra?: ExtraPart[];
    renderOrder: number;
    bloomLayer: number;
  }) {
    const add = (name: string, geo: THREE.BufferGeometry, mat: THREE.Material | THREE.Material[], neon?: NeonSpec) => {
      const g = geo.clone(); // own geometry per pool (InstancedMesh adds its own instanceMatrix + attributes)
      const im = new THREE.InstancedMesh(g, mat, cap);
      im.count = 0;
      im.frustumCulled = false;      // the factory grid is small + always near the camera — skip per-instance culling
      im.renderOrder = opts.renderOrder;
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      let emit: THREE.InstancedBufferAttribute | null = null;
      let twin: THREE.InstancedMesh | null = null;
      if (neon !== undefined) {
        emit = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3); // rgb = hue × brightness
        emit.setUsage(THREE.DynamicDrawUsage);
        g.setAttribute("instEmissive", emit);
        if (neonMasked(neon)) {
          // Per-instance bloom: this part stays OFF the bloom layer and a twin carries it, so an instance whose
          // emissive is 0 bleeds nothing. ONE extra draw call for the whole kind, whatever the building count.
          twin = new THREE.InstancedMesh(g, neonMaterial(neon, true), cap); // same geometry + attributes, one upload
          twin.count = 0;
          twin.frustumCulled = false;
          twin.renderOrder = opts.renderOrder;
          twin.instanceMatrix = im.instanceMatrix;        // shared matrices — setMatrix/add/remove reach both
          twin.layers.set(opts.bloomLayer);               // bloom pass ONLY; never drawn in the main pass
          root.add(twin);
        } else {
          im.layers.enable(opts.bloomLayer);
        }
      }
      root.add(im);
      this.parts.push({ name, im, emit, twin });
    };
    for (const p of model.parts) {
      const hue = opts.neon?.[p.name];
      if (hue !== undefined) { add(p.name, p.geometry, neonMaterial(hue), hue); continue; }
      const mats = p.colors.map((c, i) => {
        const op = p.opacities[i] ?? 1;
        return new THREE.MeshStandardMaterial({ color: c, roughness: 0.7, transparent: op < 1, opacity: op, depthWrite: op >= 1 });
      });
      add(p.name, p.geometry, mats.length === 1 ? mats[0] : mats);
    }
    for (const e of opts.extra ?? []) add(e.name, e.geometry, e.material.clone()); // clone → pool.dispose() won't kill the shared original
  }

  private part(name: string): PartIM | undefined { return this.parts.find((p) => p.name === name); }

  /** Reserve a slot, stamp `matrix` on every part, return the slot index — or -1 if the pool is FULL, which the
   *  caller must handle by putting that one building on the Group path. An InstancedMesh whose `count` runs past
   *  its instanceMatrix reads the buffer out of bounds: measured on this project, one instance over cap turns
   *  EVERY subsequent frame into a GL INVALID_OPERATION and the whole factory stops drawing. */
  add(matrix: THREE.Matrix4, owner: { inst?: { slot: number } | null }): number {
    if (this.count >= this.cap) return -1;
    const slot = this.count++;
    this.owners[slot] = owner;
    for (const p of this.parts) {
      p.im.setMatrixAt(slot, matrix);
      p.im.count = this.count;
      if (p.twin) p.twin.count = this.count;
      p.im.instanceMatrix.needsUpdate = true;
      // Seed FULL brightness at the baked hue — a machine placed and never animated must look right immediately.
      // (Kinds that idle dimmer than that overwrite it at placement from their `rest` table; see Factory.place.)
      if (p.emit) { p.emit.array[slot * 3] = 1; p.emit.array[slot * 3 + 1] = 1; p.emit.array[slot * 3 + 2] = 1; p.emit.needsUpdate = true; }
    }
    return slot;
  }

  /** Free a slot by moving the last instance into it (swap-remove) and fixing up that building's stored slot. */
  remove(slot: number): void {
    const last = --this.count;
    for (const p of this.parts) {
      if (slot !== last) {
        p.im.getMatrixAt(last, this._m); p.im.setMatrixAt(slot, this._m);
        // the emissive rides along with the matrix, or bulldozing one building steals another's glow
        if (p.emit) for (let i = 0; i < 3; i++) p.emit.array[slot * 3 + i] = p.emit.array[last * 3 + i];
      }
      p.im.count = this.count;
      if (p.twin) p.twin.count = this.count;
      p.im.instanceMatrix.needsUpdate = true;
      if (p.emit) p.emit.needsUpdate = true;
    }
    if (slot !== last) { const o = this.owners[last]; this.owners[slot] = o; if (o && o.inst) o.inst.slot = slot; }
    this.owners.length = this.count;
  }

  setMatrix(slot: number, part: string, m: THREE.Matrix4): void {
    const p = this.part(part); if (!p) return;
    p.im.setMatrixAt(slot, m); p.im.instanceMatrix.needsUpdate = true; // twin shares the attribute object
  }
  /** Brightness at the part's baked hue (the classic scalar path). */
  setEmissive(slot: number, part: string, v: number): void {
    this.setEmissiveColor(slot, part, v, v, v);
  }
  /** Full per-instance emissive COLOR. Components are LINEAR (three's working space) and multiply the material's
   *  baked emissive — so on a white-emissive part they ARE the hue × brightness. */
  setEmissiveColor(slot: number, part: string, r: number, g: number, b: number): void {
    const p = this.part(part); if (!p || !p.emit) return;
    const a = p.emit.array as Float32Array, i = slot * 3;
    a[i] = r; a[i + 1] = g; a[i + 2] = b; p.emit.needsUpdate = true;
  }
  dispose(root: THREE.Group): void {
    for (const p of this.parts) {
      root.remove(p.im); p.im.geometry.dispose();
      const mm = p.im.material; if (Array.isArray(mm)) mm.forEach((x) => x.dispose()); else (mm as THREE.Material).dispose();
      if (p.twin) { root.remove(p.twin); (p.twin.material as THREE.Material).dispose(); } // geometry is p.im's, already disposed
    }
    this.parts.length = 0; this.owners.length = 0; this.count = 0;
  }
}

/** Owns one Pool per model key. Pools are created lazily on first placement of a kind. */
export class ModelInstancer {
  private pools = new Map<string, Pool>();
  constructor(private root: THREE.Group, private renderOrder: number, private bloomLayer: number) {}

  /** Create (once) the pool for `key`, then reserve a slot for a building at `matrix`. Returns -1 if that pool is
   *  already at `cap` — the caller renders that building the slow way rather than overflowing the pool. */
  add(key: string, model: FactoryModel, matrix: THREE.Matrix4, owner: { inst?: { slot: number } | null },
      opts?: { neon?: Record<string, NeonSpec>; extra?: ExtraPart[]; cap?: number }): number {
    let pool = this.pools.get(key);
    if (!pool) {
      pool = new Pool(this.root, model, opts?.cap ?? 320, { neon: opts?.neon, extra: opts?.extra, renderOrder: this.renderOrder, bloomLayer: this.bloomLayer });
      this.pools.set(key, pool);
    }
    return pool.add(matrix, owner);
  }
  remove(key: string, slot: number): void { this.pools.get(key)?.remove(slot); }
  setMatrix(key: string, slot: number, part: string, m: THREE.Matrix4): void { this.pools.get(key)?.setMatrix(slot, part, m); }
  setEmissive(key: string, slot: number, part: string, v: number): void { this.pools.get(key)?.setEmissive(slot, part, v); }
  setEmissiveColor(key: string, slot: number, part: string, r: number, g: number, b: number): void {
    this.pools.get(key)?.setEmissiveColor(slot, part, r, g, b);
  }
  clear(): void { for (const p of this.pools.values()) p.dispose(this.root); this.pools.clear(); }
}
